import { ShellHeader } from '@/app/shell-header';
import { UnsavedGuard } from '@/components/unsaved-guard';
import { CATEGORIES, GROUPS, RESOURCES } from '@/data/groups';
import {
  Button,
  ConfirmDialog,
  Counter,
  ListPage,
  PageHeader,
  UnsavedDot,
} from '@mgrvtechgroup/admin-ds';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { GroupList } from './group-list';
import { ItemTable, type ItemRow } from './item-table';
import {
  applyOrder,
  commitDraft,
  createDraft,
  isDirty,
  moveId,
  summarizeChanges,
  type OrderDraft,
  type SavedOrder,
} from './sortable-model';

const GROUP_ROWS = GROUPS.map((group) => ({ id: group.id, name: group.name }));
const ITEM_ROWS: Record<string, ItemRow[]> = Object.fromEntries(
  GROUPS.map((group) => [
    group.id,
    RESOURCES.filter((resource) => resource.groupId === group.id).map((resource) => ({
      id: resource.id,
      code: resource.code,
      category: CATEGORIES.find((category) => category.id === resource.categoryId)?.name ?? '-',
    })),
  ]),
);

const INITIAL_ORDER: SavedOrder = {
  groups: GROUPS.map((group) => group.id),
  items: Object.fromEntries(
    Object.entries(ITEM_ROWS).map(([groupId, rows]) => [groupId, rows.map((row) => row.id)]),
  ),
};

type Confirm = { kind: 'save' } | { kind: 'switch'; groupId: string } | null;

/**
 * 순서 편집 패턴 — 왼쪽 그룹 목록, 오른쪽 선택한 그룹의 하위 항목 표. 둘 다 순서를 바꿀 수 있다.
 * - 평소엔 읽기 모드. "순서 편집"을 눌러야 드래그 핸들이 생기므로 스크롤·클릭 중 실수로 순서가 바뀌지 않는다.
 * - 편집 중 바뀐 순서는 초안(draft)에만 반영하고, 저장은 확인창을 거친 뒤에만 확정한다. 취소하면 초안을 버린다.
 * - 편집 중 다른 그룹으로 옮기면(오른쪽 표가 바뀌므로) 초안 폐기 여부를 먼저 묻는다. 다른 페이지 이동·새로고침도 막고 묻는다.
 */
export function SortablePage() {
  const [saved, setSaved] = useState(INITIAL_ORDER);
  const [selectedId, setSelectedId] = useState(INITIAL_ORDER.groups[0] ?? '');
  // null이면 읽기 모드
  const [draft, setDraft] = useState<OrderDraft | null>(null);
  const [confirm, setConfirm] = useState<Confirm>(null);

  const editing = draft !== null;
  const dirty = draft !== null && isDirty(draft, saved);

  const groupOrder = draft?.groups ?? saved.groups;

  const groupRows = useMemo(
    () =>
      applyOrder(GROUP_ROWS, groupOrder).map((group) => ({
        ...group,
        itemCount: ITEM_ROWS[group.id]?.length ?? 0,
      })),
    [groupOrder],
  );
  const itemRows = useMemo(
    () => applyOrder(ITEM_ROWS[selectedId] ?? [], draft?.items ?? saved.items[selectedId] ?? []),
    [selectedId, draft, saved],
  );
  const selectedName = GROUP_ROWS.find((group) => group.id === selectedId)?.name ?? '';

  const startEdit = () => {
    setDraft(createDraft(saved, selectedId));
  };
  const cancelEdit = () => {
    setDraft(null);
    setConfirm(null);
  };

  const selectGroup = (groupId: string) => {
    if (groupId === selectedId) {
      return;
    }
    if (dirty) {
      setConfirm({ kind: 'switch', groupId });
      return;
    }
    setSelectedId(groupId);
    // 바뀐 게 없으면 편집 모드를 유지한 채 새 그룹의 항목으로 초안만 다시 만든다.
    if (draft) {
      setDraft(createDraft(saved, groupId));
    }
  };

  const confirmSwitch = (groupId: string) => {
    setSelectedId(groupId);
    setDraft(null);
    setConfirm(null);
    toast.info('저장하지 않은 순서 변경을 취소했습니다.');
  };

  const save = () => {
    if (!draft) {
      return;
    }
    setSaved(commitDraft(saved, draft));
    setDraft(null);
    setConfirm(null);
    toast.success('순서를 저장했습니다.');
  };

  const changes = draft ? summarizeChanges(draft, saved) : { groups: 0, items: 0 };
  const consequence = [
    changes.groups > 0 ? `그룹 ${String(changes.groups)}개` : null,
    changes.items > 0 ? `${selectedName}의 하위 항목 ${String(changes.items)}개` : null,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <ListPage>
      <ShellHeader>
        <PageHeader
          title={<h1 className="title-md">순서 편집</h1>}
          description="목록의 순서를 드래그로 바꾸는 페이지입니다. 그룹을 고르면 하위 항목 표가 열리고, 순서 편집 모드에서 핸들을 끌거나 키보드로 옮깁니다."
        />
      </ShellHeader>

      <ListPage.Toolbar
        left={
          editing ? (
            <p className="body text-muted-foreground flex items-center gap-2">
              {dirty && <UnsavedDot />}
              {dirty
                ? '저장하지 않은 순서 변경이 있습니다.'
                : '핸들을 끌어 순서를 바꾸세요. 키보드는 핸들에서 Space, 방향키, Space 순서로 옮깁니다.'}
            </p>
          ) : (
            <p className="body text-muted-foreground">순서 편집을 눌러 순서를 바꿉니다.</p>
          )
        }
        right={
          editing ? (
            <>
              <Button size="sm" variant="outline" onClick={cancelEdit}>
                취소
              </Button>
              <Button
                size="sm"
                tone="brand"
                disabled={!dirty}
                onClick={() => {
                  setConfirm({ kind: 'save' });
                }}
              >
                저장
              </Button>
            </>
          ) : (
            <Button size="sm" tone="brand" onClick={startEdit}>
              순서 편집
            </Button>
          )
        }
      />

      <ListPage.Body className="border-t">
        {/* 좁은 폭에서는 그룹 목록이 위, 표가 아래로 쌓인다. */}
        <div className="flex min-h-full flex-col md:flex-row">
          <section
            aria-label="그룹"
            className="shrink-0 border-b md:w-72 md:border-r md:border-b-0"
          >
            <h2 className="title-sm flex items-center gap-2 border-b px-4 py-3">
              그룹 <Counter>{groupRows.length}</Counter>
            </h2>
            <GroupList
              groups={groupRows}
              selectedId={selectedId}
              editing={editing}
              onSelect={selectGroup}
              onMove={(activeId, overId) => {
                setDraft((prev) =>
                  prev ? { ...prev, groups: moveId(prev.groups, activeId, overId) } : prev,
                );
              }}
            />
          </section>

          <section aria-label={`${selectedName} 하위 항목`} className="min-w-0 flex-1">
            <h2 className="title-sm flex items-center gap-2 border-b px-4 py-3">
              {selectedName} 하위 항목 <Counter>{itemRows.length}</Counter>
            </h2>
            <ItemTable
              items={itemRows}
              editing={editing}
              onMove={(activeId, overId) => {
                setDraft((prev) =>
                  prev ? { ...prev, items: moveId(prev.items, activeId, overId) } : prev,
                );
              }}
            />
          </section>
        </div>
      </ListPage.Body>

      <ConfirmDialog
        open={confirm?.kind === 'save'}
        onOpenChange={(open) => {
          if (!open) {
            setConfirm(null);
          }
        }}
        target="변경한 순서"
        action="저장"
        consequence={`${consequence}의 위치가 바뀝니다.`}
        variant="default"
        onConfirm={save}
      />
      <ConfirmDialog
        open={confirm?.kind === 'switch'}
        onOpenChange={(open) => {
          if (!open) {
            setConfirm(null);
          }
        }}
        target="저장하지 않은 순서 변경"
        action="취소하고 이동"
        consequence="다른 그룹으로 이동하면 지금까지 바꾼 순서가 원래대로 돌아갑니다."
        variant="destructive"
        onConfirm={() => {
          if (confirm?.kind === 'switch') {
            confirmSwitch(confirm.groupId);
          }
        }}
      />
      <UnsavedGuard when={dirty} />
    </ListPage>
  );
}
